import { CustomEase, TimelineLite, TimelineMax } from 'gsap/all';
import * as THREE from 'three';

import { FACE_TRANSITION_CONST, FACE_TRANSITION_PHASES, PARTICLES_CONFIG } from "../../../const";


const glslify = require('glslify');

export default class Particles {

	constructor(webgl) {
		this.webgl = webgl;
		this.container = new THREE.Object3D();
	}

  updateValues(uSize, uDepth, uRandom, uCurl) {
    const tl = new TimelineLite({
      paused: true,
    })
      .to(this.object3D.material.uniforms.uSize, 0.6, { value: uSize })
		  .to(this.object3D.material.uniforms.uDepth, 0.6, { value: uDepth }, '-=0.6')
      .to(this.object3D.material.uniforms.uRandom, 0.6, { value: uRandom }, '-=0.6')
      .to(this.object3D.material.uniforms.uCurl, 0.6, { value: uCurl }, '-=0.6');

    tl.play();
  }

	init(src, isDefault = false) {
    this.destroy();

		const loader = new THREE.TextureLoader();

		loader.load(src, (texture) => {
			this.texture = texture;

      const ratio = texture.image.height / texture.image.width;

      this.texture.image.width =  (window.innerHeight * 0.8) / ratio;
      this.texture.image.height = window.innerHeight * 0.8;

			this.texture.minFilter = THREE.LinearMipmapLinear;
			this.texture.magFilter = THREE.LinearFilter;

			this.texture.format = THREE.LuminanceFormat;

			this.width = texture.image.width;
			this.height = texture.image.height;

			this.initPoints(true);
			this.resize();
      this.show(isDefault);

      if (this.webgl.app.gui) this.webgl.app.gui.update();
		});
	}

	initPoints(discard) {
		this.numPoints = this.width * this.height;

		let numVisible = this.numPoints;
		let threshold = 0;
		let originalColors;

		if (discard) {
			// discard pixels darker than threshold #22
			numVisible = 0;
			threshold = 1;

			const img = this.texture.image;
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			canvas.width = this.width;
			canvas.height = this.height;

			ctx.scale(1, -1);
			ctx.drawImage(img, 0, 0, this.width, this.height * -1);

			const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

			originalColors = Float32Array.from(imgData.data);

			for (let i = 0; i < this.numPoints; i++) {
				if (originalColors[i * 4 + 3] > threshold) numVisible++;
			}
		}

    const {
      uCurl: { min: uCurlValue },
      uDepth: { min: uDepthValue },
      uRandom: { min: uRandomValue },
      uSize: { min: uSizeValue },
      uContrast: { min: uContrastValue },
      uScale: { min: uScaleValue },
    } = PARTICLES_CONFIG;

		const uniforms = {
			uTime: { value: 0 },
			uRandom: { value: uRandomValue },
			uCurl: { value: uCurlValue },
      uContrast: { value: uContrastValue },
			uScale: { value: uScaleValue },
			uDepth: { value: uDepthValue },
			uSize: { value: uSizeValue },
			uSizeScale: { value: 1. },
			uSizeScale0: { value: 0. },
			uSizeScale1: { value: 0. },
			uSizeScale2: { value: 0. },
			uTextureSize: { value: new THREE.Vector2(this.width, this.height) },
			uTexture: { value: this.texture },
      uMap: { value: new THREE.TextureLoader().load('/img/map2.jpg') },
			uTouch: { value: null },
      grayscaleR: { value: 0.156 },
      grayscaleG: { value: 0.152 },
      grayscaleB: { value: 0.157 },
      uColor: { value: new THREE.Color('#9C9C9C') },
      scale: { value: 1 },
      finalColor: { value: 1.0 },
		};

		const material = new THREE.ShaderMaterial({
			uniforms,
			vertexShader: glslify(require('../../shaders/particle.vert')),
			fragmentShader: glslify(require('../../shaders/particle.frag')),
			depthTest: false,
			transparent: true,
		});

		const geometry = new THREE.BufferGeometry();

		const indices = new Float32Array(numVisible);
		const offsets = new Float32Array(numVisible * 3);
		const angles = new Float32Array(numVisible);
		const randoms = new Float32Array(numVisible);

		for (let i = 0, j = 0; i < this.numPoints; i++) {
			if (discard && originalColors[i * 4 + 3] <= threshold) continue;

			offsets[j * 3 + 0] = i % this.width;
			offsets[j * 3 + 1] = Math.floor(i / this.width);
      offsets[j * 3 + 2] = 0;

			indices[j] = i;

			angles[j] = Math.random() * Math.PI;
			randoms[j] = Math.random() ;

			j++;
		}

		geometry.setAttribute('position', new THREE.BufferAttribute(offsets, 3));
		geometry.setAttribute('pindex', new THREE.BufferAttribute(indices, 1));
		geometry.setAttribute('offset', new THREE.BufferAttribute(offsets, 3));

		geometry.setAttribute('randoms', new THREE.BufferAttribute(randoms, 1));
		geometry.setAttribute('angle', new THREE.BufferAttribute(angles, 1));

		this.object3D = new THREE.Points(geometry, material);
		this.container.add(this.object3D);
	}

	update(delta) {
		if (!this.object3D) return;
		this.object3D.material.uniforms.uTime.value += delta;
	}

  goToPhase(index = 1) {
    const { random, curl, scale, size, contrast } = FACE_TRANSITION_PHASES[index];
    const { random: prevRandom, curl: prevCurl, scale: prevScale, size: prevSize, contrast: prevContrast } = FACE_TRANSITION_PHASES[0];

    const { depthMax, randomMax } = FACE_TRANSITION_CONST;

    // const fromRandom = getRandomNumber(randomMax, randomMax * 1.2);
    // const fromCurl = getRandomNumber(curl * 0.5, curl);
    // const fromSize = getRandomNumber(size, size * 1.2);

    this.animation.pause()

    const speed = 1;
    // TODO: add scale & contrast
    new TimelineMax({
      onStart: () => this.animation.pause(),
    })
      .set(this.object3D.material.uniforms.uRandom, { value: prevRandom })
      .set(this.object3D.material.uniforms.uCurl, { value: prevCurl })
      .set(this.object3D.material.uniforms.uSize, { value: prevSize })
      // .set(this.object3D.material.uniforms.uScale, { value: prevScale })
      .set(this.object3D.material.uniforms.uContrast, { value: prevContrast })
      .set(this.webgl.camera.position, { z: 300 })

      .to(this.webgl.camera.position, 10.3, { z: 180, ease: CustomEase.create("custom", "M0,0 C0,0 0.156,-0.01 0.55,0.06 1.036,0.146 1,1 1,1") })
    ;
  }

	show(isDefault) {
    const time = 1.0;

    const { randomMax, depthMax, depthMin } = FACE_TRANSITION_CONST;
    const { random, curl, scale, size, contrast } = FACE_TRANSITION_PHASES[0];

    const duration = 2.5;

    new TimelineLite({
      onStart: () => {
        this.animation = new TimelineMax({ repeat: -1, repeatDelay: 0, yoyo: true })
          // .to(this.object3D.material.uniforms.uRandom, duration, { value: 1, ease: 'Quad.easeIn' }, '-=1')
          // .to(this.object3D.material.uniforms.uCurl, duration, { value: 0.2, ease: 'Quad.easeIn' }, `-=${duration}`)
          // .to(this.object3D.material.uniforms.uScale, duration, { value: 1, ease: 'Quad.easeIn' }, `-=${duration}`)
          .to(this.object3D.material.uniforms.uRandom, duration, { value: 7, ease: 'Quad.easeIn' })
          .to(this.object3D.material.uniforms.uCurl, duration, { value: 0.2, ease: 'Quad.easeIn' }, `-=${duration}`)
          .to(this.object3D.material.uniforms.uScale, duration, { value: 1, ease: 'Quad.easeIn' }, `-=${duration}`)
          .to(this.object3D.material.uniforms.uRandom, duration, { value: random - 7, ease: 'Quad.easeIn' })
          .to(this.object3D.material.uniforms.uCurl, duration, { value: curl, ease: 'Quad.easeIn' }, `-=${duration}`)
          .to(this.object3D.material.uniforms.uScale, duration, { value: 0, ease: 'Quad.easeIn' }, `-=${duration}`)
        ;
      }
    })
      .to(this.object3D.material.uniforms.uRandom, time, { value: randomMax })

      .to(this.object3D.material.uniforms.uRandom, time, { value: isDefault ? 1 : random })
      .to(this.object3D.material.uniforms.uCurl, time, { value: isDefault ? 0 : curl }, `-=${time}`)
      .to(this.object3D.material.uniforms.uContrast, time, { value: isDefault ? 1.44 : contrast }, `-=${time}`)
      .to(this.object3D.material.uniforms.uScale, time, { value: isDefault ? 0 : scale }, `-=${time}`)
      .to(this.object3D.material.uniforms.uSize, time, { value: isDefault ? 0.73 : size }, `-=${time}`)
      .fromTo(this.object3D.material.uniforms.uDepth, time, { value: isDefault ? 1 : depthMin }, { value: depthMax, ease: 'Quad.easeIn' }, `-=${time}`)
    ;
	}

	destroy() {
		if (!this.object3D) return;

		this.object3D.parent.remove(this.object3D);
		this.object3D.geometry.dispose();
		this.object3D.material.dispose();
		this.object3D = null;

    this.webgl.camera.position.z = 300;
	}

	resize() {
		if (!this.object3D) return;

		const scale = this.webgl.fovHeight / this.height;
		this.object3D.scale.set(scale, scale, 1);
		// this.object3D.position.y = this.height * (0.155 * scale);
	}
}
